import { useEffect, useRef, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { Download, Link } from "lucide-react";
import { db } from "../firebase-config";
import MoveIcon from "./MoveIcon";
import AvatarRing from "./AvatarRing";
import ShareableResultCard from "./ShareableResultCard";
import { getEquippedItem } from "../cosmetics";
import { shareImageToPlatform, SITE_URL } from "../shareImage";
import { TRUST_COLOR, BETRAY_COLOR, MIXED_COLOR, MIXED_BG, TRUST_BG, BETRAY_BG, TEXT_MUTED, BORDER } from "../theme";

function XIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ display: "block" }}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.259 5.632L18.244 2.25zM17.083 19.77h1.833L7.084 4.126H5.117L17.083 19.77z" />
    </svg>
  );
}
function WhatsAppIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ display: "block" }}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}
function FacebookIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ display: "block" }}>
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function SharePlatformBtn({ label, icon, bg, onClick, active }) {
  return (
    <button
      onClick={onClick}
      disabled={!!active}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        gap: "5px", padding: "10px 4px",
        background: bg, color: "#fff", border: "none", borderRadius: "8px",
        fontSize: "0.625rem", fontWeight: "700", opacity: active ? 0.6 : 1,
      }}
    >
      {icon}
      {active ? "…" : label}
    </button>
  );
}

function Results({ user, userProfile, game, shopItems = [], returnToMenu }) {
  const [opponentProfile, setOpponentProfile] = useState(null);
  const [stage, setStage] = useState("revealing");
  const [sharing, setSharing] = useState(null); // which platform is actively generating
  const shareCardRef = useRef(null);

  const isPlayerA = game.playerA === user.uid;
  const myMove = isPlayerA ? game.playerAMove : game.playerBMove;
  const opponentMove = isPlayerA ? game.playerBMove : game.playerAMove;
  const myScore = isPlayerA ? game.playerAScore : game.playerBScore;
  const opponentScore = isPlayerA ? game.playerBScore : game.playerAScore;

  useEffect(() => {
    const opponentId = isPlayerA ? game.playerB : game.playerA;
    getDoc(doc(db, "users", opponentId)).then((snapshot) => {
      if (snapshot.exists()) setOpponentProfile(snapshot.data());
    });
  }, [game, isPlayerA]);

  useEffect(() => {
    if (!opponentProfile) return;
    const timeouts = [
      setTimeout(() => setStage("mine"), 900),
      setTimeout(() => setStage("theirs"), 2100),
      setTimeout(() => setStage("result"), 3200),
    ];
    return () => timeouts.forEach(clearTimeout);
  }, [opponentProfile]);

  const getOutcomeText = () => {
    if (myMove === "cooperate" && opponentMove === "cooperate") return "you both trusted";
    if (myMove === "defect" && opponentMove === "defect") return "you both betrayed";
    if (myMove === "cooperate" && opponentMove === "defect") return "you were betrayed";
    return "you betrayed them";
  };

  const getOutcomeColors = () => {
    if (myMove === "cooperate" && opponentMove === "cooperate") return { color: TRUST_COLOR, bg: TRUST_BG };
    if (myMove === "defect" && opponentMove === "defect") return { color: BETRAY_COLOR, bg: BETRAY_BG };
    return { color: MIXED_COLOR, bg: MIXED_BG };
  };

  const shareText = () => `${getOutcomeText()} on trust game`;

  const doShare = async (platform) => {
    setSharing(platform);
    const result = await shareImageToPlatform(shareCardRef.current, {
      platform,
      text: shareText(),
      url: SITE_URL,
    });
    setSharing(null);
    if (!result.ok) alert(result.error);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(SITE_URL).then(() => alert("link copied!"));
  };

  const playAgain = async () => {
    returnToMenu({ myMove, opponentMove, myScore, opponentScore });
  };

  if (!opponentProfile) return <p style={{ textAlign: "center", marginTop: "40px", color: TEXT_MUTED }}>loading results...</p>;

  const revealedMine   = stage !== "revealing";
  const revealedTheirs = stage === "theirs" || stage === "result";
  const showResult     = stage === "result";
  const outcomeColors  = getOutcomeColors();
  const borderItem     = getEquippedItem(userProfile, shopItems, "border");
  const opponentBorderItem = getEquippedItem(opponentProfile, shopItems, "border");

  return (
    <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      {/* ShareableResultCard rendered off-screen for image capture only */}
      <div style={{ position: "fixed", left: "-9999px", top: 0, width: "380px", zIndex: -1 }}>
        <div ref={shareCardRef}>
          <ShareableResultCard
            userProfile={userProfile}
            opponentProfile={opponentProfile}
            myMove={myMove}
            opponentMove={opponentMove}
            myScore={myScore}
            opponentScore={opponentScore}
            outcomeText={getOutcomeText()}
            outcomeColors={outcomeColors}
            borderItem={borderItem}
            opponentBorderItem={opponentBorderItem}
          />
        </div>
      </div>

      <div style={{ width: "100%", maxWidth: "440px" }}>
        {/* Reveal stage */}
        <div style={{ display: "flex", justifyContent: "space-around", alignItems: "center", marginBottom: "20px" }}>
          <RevealSlot photoURL={userProfile.photoURL} move={myMove} revealed={revealedMine} ringColor={TRUST_COLOR} borderItem={borderItem} />
          <div style={{ fontSize: "0.75rem", color: TEXT_MUTED, fontWeight: "600" }}>vs</div>
          <RevealSlot photoURL={opponentProfile.photoURL} move={opponentMove} revealed={revealedTheirs} ringColor={BORDER} flip={opponentMove === "defect"} borderItem={opponentBorderItem} />
        </div>

        {!showResult && (
          <div style={{ textAlign: "center", fontSize: "0.8125rem", color: TEXT_MUTED, marginBottom: "10px" }}>
            revealing...
          </div>
        )}

        {showResult && (
          <>
            <div style={{ textAlign: "center", marginBottom: "20px", animation: "fadeIn 0.3s ease" }}>
              <span style={{ display: "inline-block", padding: "9px 22px", borderRadius: "20px", background: outcomeColors.bg, color: outcomeColors.color, fontSize: "0.9375rem", fontWeight: "700" }}>
                {getOutcomeText()}
              </span>
            </div>

            <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
              <div style={{ flex: 1, textAlign: "center", padding: "14px", border: `1px solid ${BORDER}`, borderRadius: "10px" }}>
                <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED, marginBottom: "4px" }}>you</div>
                <div style={{ fontSize: "1.625rem", fontWeight: "700" }}>+{myScore}</div>
                <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED }}>total {userProfile.totalScore || 0}</div>
              </div>
              <div style={{ flex: 1, textAlign: "center", padding: "14px", border: `1px solid ${BORDER}`, borderRadius: "10px" }}>
                <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED, marginBottom: "4px" }}>them</div>
                <div style={{ fontSize: "1.625rem", fontWeight: "700" }}>+{opponentScore}</div>
                <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED }}>total {opponentProfile.totalScore || 0}</div>
              </div>
            </div>

            {/* Share row */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "8px", marginBottom: "10px" }}>
              <SharePlatformBtn label="tweet" icon={<XIcon size={17} />} bg="#000" onClick={() => doShare("twitter")} active={sharing === "twitter"} />
              <SharePlatformBtn label="facebook" icon={<FacebookIcon size={17} />} bg="#1877F2" onClick={() => doShare("facebook")} active={sharing === "facebook"} />
              <SharePlatformBtn label="whatsapp" icon={<WhatsAppIcon size={17} />} bg="#25D366" onClick={() => doShare("whatsapp")} active={sharing === "whatsapp"} />
              <SharePlatformBtn label="image" icon={<Download size={17} />} bg={TRUST_COLOR} onClick={() => doShare("image")} active={sharing === "image"} />
            </div>
            <button onClick={copyLink} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "9px", marginBottom: "14px", fontSize: "0.8125rem", fontWeight: "600", background: "#fff", color: TEXT_MUTED, border: `1px solid ${BORDER}`, borderRadius: "8px" }}>
              <Link size={14} /> copy link
            </button>

            <button onClick={playAgain} style={{ padding: "14px", fontSize: "0.9375rem", background: TRUST_COLOR, color: "white", border: "none", borderRadius: "10px", width: "100%", fontWeight: "700" }}>
              continue
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function RevealSlot({ photoURL, move, revealed, ringColor, flip = false, borderItem }) {
  return (
    <div style={{ position: "relative", width: "92px", height: "92px", filter: revealed ? "none" : "blur(2px) grayscale(0.4)", opacity: revealed ? 1 : 0.6, transition: "all 0.3s ease" }}>
      <AvatarRing photoURL={photoURL} size={92} item={borderItem} fallbackBorder={`3px solid ${ringColor}`} />
      {revealed && (
        <div style={{ position: "absolute", bottom: "-10px", right: "-10px", background: "#fff", borderRadius: "50%", padding: "8px", boxShadow: "0 2px 6px rgba(0,0,0,0.15)", animation: "reveal-pop 0.35s ease" }}>
          <MoveIcon move={move} size={28} flip={flip} />
        </div>
      )}
    </div>
  );
}

export default Results;
